import { useMemo } from 'react';
import type { TranslationFunction } from '@/app/components/translations/Translation';
import type { StarfishIconRef } from '@/app/common/icons/starfish';

export type IconName =
  | 'healthy_safety'
  | 'wifi'
  | 'parking'
  | 'pets'
  | 'adults_only'
  | 'child'
  | 'accessibility'
  | 'language'
  | 'drink'
  | 'pool'
  | 'sports'
  | 'tv2'
  | 'business'
  | 'bathtub'
  | 'cleaning'
  | 'reception'
  | 'info';

export const FACILITY_ICONS: Record<IconName, StarfishIconRef> = {
  healthy_safety: { category: 'Services', name: 'icon-sanitizer', color: 'iconPrimary' },
  wifi: { category: 'Services', name: 'icon-wifi', color: 'iconPrimary' },
  parking: { category: 'Services', name: 'icon-parking', color: 'iconPrimary' },
  pets: { category: 'Services', name: 'icon-pawn', color: 'iconPrimary' },
  adults_only: { category: 'Services', name: 'icon-adults-only', color: 'iconPrimary' },
  child: { category: 'Services', name: 'icon-child', color: 'iconPrimary' },
  accessibility: { category: 'People and users', name: 'icon-accessible-service', color: 'iconPrimary' },
  language: { category: 'UI Essentials', name: 'icon-language', color: 'iconPrimary' },
  drink: { category: 'Food and beverages', name: 'icon-cutlery', color: 'iconPrimary' },
  pool: { category: 'Services', name: 'icon-swimming-pool', color: 'iconPrimary' },
  // The design system has no facilities glyph, so the whole activities category shows tennis.
  sports: { category: 'Services', name: 'icon-tennis', color: 'iconPrimary' },
  tv2: { category: 'Services', name: 'icon-media', color: 'iconPrimary' },
  business: { category: 'Services', name: 'icon-briefcase', color: 'iconPrimary' },
  bathtub: { category: 'Services', name: 'icon-bathtub', color: 'iconPrimary' },
  cleaning: { category: 'Services', name: 'icon-broom', color: 'iconPrimary' },
  reception: { category: 'Services', name: 'icon-reception', color: 'iconPrimary' },
  info: { category: 'Semantics', name: 'icon-circle-info', color: 'iconPrimary' },
};

export type HotelServiceCategory = {
  translationKey: string;
  icon: IconName;
  position: number;
};

export type ExtendedSortedService = {
  translationKey: string;
  icon: IconName;
  position: number;
  category: HotelServicesCategories;
  services: string[];
};

export const hotelServiceCategoryMap: { [key in HotelServicesCategories]: HotelServiceCategory } = {
  SWIMMING_POOL: { translationKey: 'vacation.common.hotelservicetype.pool', icon: 'pool', position: 1 },
  INTERNET: { translationKey: 'vacation.common.hotelservicetype.internet', icon: 'wifi', position: 2 },
  CHILDREN: { translationKey: 'vacation.common.hotelservicetype.children', icon: 'child', position: 3 },
  PARKING: { translationKey: 'vacation.common.hotelservicetype.parking', icon: 'parking', position: 4 },
  HEALTH_SAFETY: { translationKey: 'vacation.select.health.safety', icon: 'healthy_safety', position: 5 },
  PETS: { translationKey: 'vacation.common.hotelservicetype.pets', icon: 'pets', position: 6 },
  ADULTS_ONLY: { translationKey: 'vacation.common.hotelservicetype.adultonly', icon: 'adults_only', position: 7 },
  ACCESSIBILITY: {
    translationKey: 'vacation.common.hotelservicetype.accessibility',
    icon: 'accessibility',
    position: 8,
  },
  LANGUAGES_SPOKEN: {
    translationKey: 'vacation.common.hotelservicetype.languagespoken',
    icon: 'language',
    position: 9,
  },
  FOOD_AND_DRINK: { translationKey: 'vacation.common.hotelservicetype.fooddrink', icon: 'drink', position: 10 },
  ACTIVITIES: {
    translationKey: 'vacation.common.hotelservicetype.activities',
    icon: 'sports',
    position: 11,
  },
  MEDIA_AND_TECHNOLOGY: {
    translationKey: 'vacation.common.hotelservicetype.technology',
    icon: 'tv2',
    position: 12,
  },
  BUSINESS_EQUIPMENT: {
    translationKey: 'vacation.common.hotelservicetype.businessequipment',
    icon: 'business',
    position: 13,
  },
  BATHROOM: { translationKey: 'vacation.common.hotelservicetype.bathroom', icon: 'bathtub', position: 14 },
  CLEANING_SERVICES: {
    translationKey: 'vacation.common.hotelservicetype.cleaningservices',
    icon: 'cleaning',
    position: 15,
  },
  RECEPTION_SERVICES: {
    translationKey: 'vacation.common.hotelservicetype.receptionservices',
    icon: 'reception',
    position: 16,
  },
  GENERAL: { translationKey: 'vacation.common.hotelservicetype.general', icon: 'info', position: 17 },
} as const;

export function useExtendedSortedHotelServices(services: HotelServiceGroup[] | undefined): ExtendedSortedService[] {
  const memoizedValue = useMemo(() => {
    const sortedServiceCategories = services
      ? services
          .filter((serviceCategory) => hotelServiceCategoryMap[serviceCategory.category] !== undefined)
          .map((serviceCategory) => ({
            ...serviceCategory,
            ...hotelServiceCategoryMap[serviceCategory.category],
          }))
          .sort((a, b) => a.position - b.position)
      : [];
    return sortedServiceCategories;
  }, [services]);
  return memoizedValue;
}

/**
 * The label shown next to a category icon in the compact facility previews: the "Most popular"
 * rows and the hero amenities summary fallback.
 *
 * A category normally surfaces its first facility (e.g. "Indoor pool" for SWIMMING_POOL), but
 * travel-api can return a category whose facilities all come with a blank label
 * (`{ type: 'Bathroom', label: '' }`); the adapter drops those, leaving the category with no
 * facility to name and the row painting an icon with no text. HEALTH_SAFETY shows the category
 * label by design — its mapping in fetchHotelStatic is commented out today, so the branch is
 * dormant rather than dead. Both fall back to the category label.
 */
export function getServicePreviewLabel(service: ExtendedSortedService, t: TranslationFunction): string {
  if (service.category === 'HEALTH_SAFETY') return t(service.translationKey);
  return service.services[0] || t(service.translationKey);
}
